import React, { useState, useEffect, useRef } from 'react';
import { isEqual } from 'lodash';
import { Button, ChevronDown, Select, Toggle } from '@opendate-app/setlist';
import { DEFAULTS } from '../../../WebOrderForm/utils/applyWidgetSettings';
import { deriveSurfaceColors } from '../../../WebOrderForm/utils/deriveInteractiveColors';
import { toastError } from '../../../../shared/toastHelper';
import ThemePicker from './ThemePicker';
import CreateEntityModal from '../../../../shared/CreateEntityModal';
const axios = require('axios').default;

// When the user changes one of these "anchor" fields, we auto-fill the
// related fields with values derived from the anchor. Manual edits to the
// derived fields stick — but the next time the anchor changes, derivation
// runs again and the manual values are overwritten ("system kicks in").
const DERIVATIONS = {
  promo_background_color: (bg) => {
    const s = deriveSurfaceColors(bg);
    return {
      promo_border_color:      s.border,
      promo_text_color:        s.text,
      promo_placeholder_color: s.placeholder
    };
  },
  cart_background_color: (bg) => {
    const s = deriveSurfaceColors(bg);
    return { cart_border_color: s.border };
  },
  quantity_stepper_fill_color: (fill) => {
    const s = deriveSurfaceColors(fill);
    return {
      quantity_stepper_border_color: s.border,
      quantity_stepper_icon_color:   s.text
    };
  }
};

const FONT_FAMILIES = [
  'Open Sans', 'Inter', 'Roboto', 'Lato', 'Poppins',
  'Montserrat', 'Playfair Display', 'Space Grotesk'
];

// All persisted appearance keys.
const APPEARANCE_KEYS = [
  // Simple-mode + shared base
  'background_color', 'button_color', 'font_color',
  'card_color', 'border_color', 'font_family', 'corner_radius',
  // Advanced-only
  'cart_background_color', 'cart_border_color',
  'promo_background_color', 'promo_border_color',
  'promo_text_color', 'promo_placeholder_color',
  'tab_active_color', 'tab_inactive_color', 'tab_border_color',
  'body_text_color', 'secondary_text_color', 'link_color',
  'quantity_stepper_border_color', 'quantity_stepper_fill_color', 'quantity_stepper_icon_color',
  'appearance_mode'
];

// Keys that participate in "is the current config edited vs the selected
// theme" detection. We exclude appearance_mode because it's a UI preference,
// not a saved-with-the-template attribute.
const APPEARANCE_DIFF_KEYS = APPEARANCE_KEYS.filter((k) => k !== 'appearance_mode');

// Project an object (form or template) onto the diff keys, normalizing
// null/undefined to null so isEqual treats them identically.
const themeAppearance = (obj) => Object.fromEntries(
  APPEARANCE_DIFF_KEYS.map((k) => {
    const v = obj && obj[k];
    return [k, (v === null || v === undefined) ? null : v];
  })
);

// Advanced sections in display order, mirroring the mockup.
const ADVANCED_SECTIONS = [
  {
    label: 'General',
    fields: [
      { key: 'button_color',     label: 'Button Color' },
      { key: 'background_color', label: 'Background' }
    ]
  },
  {
    label: 'Ticket Rows',
    fields: [
      { key: 'card_color',   label: 'Background' },
      { key: 'border_color', label: 'Border' }
    ]
  },
  {
    label: 'Cart Summary',
    fields: [
      { key: 'cart_background_color', label: 'Background' },
      { key: 'cart_border_color',     label: 'Border' }
    ]
  },
  {
    label: 'Promo Code',
    fields: [
      { key: 'promo_background_color',  label: 'Background' },
      { key: 'promo_border_color',      label: 'Border' },
      { key: 'promo_text_color',        label: 'Text' },
      { key: 'promo_placeholder_color', label: 'Placeholder' }
    ]
  },
  {
    label: 'Tab Bar',
    fields: [
      { key: 'tab_active_color',   label: 'Active Tab' },
      { key: 'tab_inactive_color', label: 'Inactive Tab' },
      { key: 'tab_border_color',   label: 'Divider' }
    ]
  },
  {
    label: 'Font Colors',
    fields: [
      { key: 'font_color',           label: 'Headings' },
      { key: 'body_text_color',      label: 'Body Text' },
      { key: 'secondary_text_color', label: 'Secondary' },
      { key: 'link_color',           label: 'Links' }
    ]
  },
  {
    label: 'Quantity Stepper',
    fields: [
      { key: 'quantity_stepper_border_color', label: 'Border' },
      { key: 'quantity_stepper_fill_color',   label: 'Fill' },
      { key: 'quantity_stepper_icon_color',   label: 'Icon' }
    ]
  }
];

// Simple controls: each is the canonical Advanced field for display,
// and the cascade list is what gets written when the user changes the
// Simple control. Mirrors mockup's simpleUpdateColor.
const SIMPLE_FIELDS = [
  { label: 'Background', canonical: 'background_color',
    cascade: ['background_color'] },
  { label: 'Active Tab', canonical: 'tab_active_color',
    cascade: ['tab_active_color'] },
  { label: 'Button',     canonical: 'button_color',
    cascade: ['button_color', 'link_color'] },
  { label: 'Font color', canonical: 'font_color',
    cascade: [
      'font_color', 'body_text_color', 'secondary_text_color',
      'promo_text_color', 'promo_placeholder_color', 'tab_inactive_color'
    ] },
  { label: 'Card',       canonical: 'card_color',
    cascade: ['card_color', 'cart_background_color'] },
  { label: 'Border',     canonical: 'border_color',
    cascade: ['border_color', 'cart_border_color', 'tab_border_color', 'promo_border_color'] }
];

const initialForm = (widgetConfig) => Object.fromEntries(
  APPEARANCE_KEYS.map(k => {
    const v = widgetConfig && widgetConfig[k];
    if (k === 'appearance_mode') {
      return [k, v || 'simple'];
    }
    return [k, (v === null || v === undefined) ? null : v];
  })
);

const pickAppearance = (obj) => Object.fromEntries(
  APPEARANCE_KEYS.filter(k => k !== 'appearance_mode').map(k => {
    const v = obj && obj[k];
    return [k, (v === null || v === undefined) ? null : v];
  })
);

const colorValue = (form, key) => {
  const v = form[key];
  if (v === null || v === undefined) return DEFAULTS[key];
  return v;
};

const HEX_RE = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

const normalizeHex = (input, fallback) => {
  if (typeof input !== 'string') return fallback;
  const trimmed = input.trim();
  if (!HEX_RE.test(trimmed)) return fallback;
  const stripped = trimmed.replace(/^#/, '');
  const expanded = stripped.length === 3
    ? stripped.split('').map((c) => c + c).join('')
    : stripped;
  return `#${expanded.toUpperCase()}`;
};

const AppearanceTab = ({
  csrfToken,
  team,
  widgetConfig,
  widgetTemplates = [],
  onWidgetConfigChange,
  onWidgetTemplatesChange
}) => {
  const [form, setForm] = useState(() => initialForm(widgetConfig));
  const [pending, setPending] = useState(false);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [expanded, setExpanded] = useState({
    colors: true,
    typography: false,
    cornerRadius: false
  });
  const isFirstRender = useRef(true);
  const latestForm = useRef(form);
  const pendingSave = useRef(false);
  latestForm.current = form;

  const teamId = team && team.id;
  const advanced = form.appearance_mode === 'advanced';

  const selectedTemplate = widgetTemplates.find(
    (t) => widgetConfig && t.id === widgetConfig.widget_template_id
  ) || null;
  const isEdited = !isEqual(themeAppearance(form), themeAppearance(selectedTemplate));

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return undefined;
    }
    pendingSave.current = true;
    const handle = setTimeout(() => {
      pendingSave.current = false;
      save(form);
    }, 500);
    return () => {
      clearTimeout(handle);
      if (pendingSave.current) {
        pendingSave.current = false;
        save(latestForm.current);
      }
    };
  // Re-run when any form field changes:
  }, [JSON.stringify(form)]);

  const save = async (next) => {
    if (!teamId) return;
    setPending(true);
    try {
      const isUpdate = Boolean(widgetConfig && widgetConfig.id);
      const url = isUpdate
        ? `/teams/${teamId}/widget_configs/${widgetConfig.id}`
        : `/teams/${teamId}/widget_configs`;
      const method = isUpdate ? 'patch' : 'post';
      const body = isUpdate
        ? { widget_config: next }
        : {
            widget_config: {
              calendar_event_id: widgetConfig && widgetConfig.calendar_event_id,
              widget_type: 'custom',
              ...next
            }
          };
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      const response = await axios({ method, url, data: body });
      onWidgetConfigChange(response.data);
    } catch (e) {
      console.error('AppearanceTab save failed', e);
      toastError('Failed to save appearance');
    } finally {
      setPending(false);
    }
  };

  const update = (key, value) => {
    setForm(prev => {
      const next = { ...prev, [key]: value };
      if (DERIVATIONS[key]) Object.assign(next, DERIVATIONS[key](value));
      return next;
    });
  };

  // Update multiple fields in one render so they save in a single PATCH
  // (the auto-save effect only depends on the JSON.stringify(form) dep).
  const updateMany = (entries) => {
    setForm(prev => {
      const next = { ...prev, ...entries };
      Object.keys(entries).forEach(k => {
        if (DERIVATIONS[k]) Object.assign(next, DERIVATIONS[k](entries[k]));
      });
      return next;
    });
  };

  const simpleUpdate = (canonical, value) => {
    const channel = SIMPLE_FIELDS.find(f => f.canonical === canonical);
    if (!channel) return update(canonical, value);
    const entries = Object.fromEntries(channel.cascade.map(k => [k, value]));
    updateMany(entries);
  };

  const setMode = (mode) => {
    if (form.appearance_mode === mode) return;
    const next = { ...form, appearance_mode: mode };
    setForm(next);
    // Persist immediately so a quick toggle-then-refresh doesn't lose
    // the user's choice within the auto-save debounce window.
    save(next);
  };

  const applyTemplate = async (templateId) => {
    if (!teamId || !widgetConfig || !widgetConfig.id) return;
    if (isEdited && !window.confirm(
      'You have unsaved changes to this theme. Applying will discard them. Continue?'
    )) return;
    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      if (!templateId) {
        const cleared = Object.fromEntries(
          APPEARANCE_KEYS.filter(k => k !== 'appearance_mode').map(k => [k, null])
        );
        setForm(prev => ({ ...prev, ...cleared }));
        const response = await axios.patch(
          `/teams/${teamId}/widget_configs/${widgetConfig.id}`,
          { widget_config: { widget_template_id: null, ...cleared } }
        );
        onWidgetConfigChange(response.data);
        return;
      }

      const template = widgetTemplates.find(t => t.id === Number(templateId));
      if (!template) return;

      const templateAppearance = pickAppearance(template);
      setForm(prev => ({ ...prev, ...templateAppearance }));
      const response = await axios.patch(
        `/teams/${teamId}/widget_configs/${widgetConfig.id}`,
        { widget_config: { widget_template_id: template.id, ...templateAppearance } }
      );
      onWidgetConfigChange(response.data);
    } catch (e) {
      console.error('AppearanceTab applyTemplate failed', e);
      toastError('Failed to apply template');
    }
  };

  const saveAsTemplate = async (name, done) => {
    if (!teamId || !name) {
      if (done) done();
      return;
    }
    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      const templateBody = pickAppearance(form);
      const templateResponse = await axios.post(
        `/teams/${teamId}/widget_templates`,
        { widget_template: { name, ...templateBody } }
      );
      const newTemplate = templateResponse.data;
      onWidgetTemplatesChange([...widgetTemplates, newTemplate]);
      setSaveModalOpen(false);

      if (widgetConfig && widgetConfig.id) {
        const configResponse = await axios.patch(
          `/teams/${teamId}/widget_configs/${widgetConfig.id}`,
          { widget_config: { widget_template_id: newTemplate.id } }
        );
        onWidgetConfigChange(configResponse.data);
      }
    } catch (e) {
      console.error('AppearanceTab saveAsTemplate failed', e);
      toastError('Failed to save template');
    } finally {
      if (done) done();
    }
  };

  const updateTemplate = async () => {
    if (!teamId || !selectedTemplate) return;

    const otherUses = Math.max((selectedTemplate.usage_count || 0) - 1, 0);
    if (otherUses > 0 && !window.confirm(
      `This theme is also used by ${otherUses} other ${otherUses === 1 ? 'event' : 'events'}. ` +
      'Updating it will restyle them too. Continue?'
    )) return;

    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      const templateBody = pickAppearance(form);
      const response = await axios.patch(
        `/teams/${teamId}/widget_templates/${selectedTemplate.id}`,
        { widget_template: templateBody }
      );
      onWidgetTemplatesChange(
        widgetTemplates.map((t) => (t.id === response.data.id ? response.data : t))
      );
    } catch (e) {
      console.error('AppearanceTab updateTemplate failed', e);
      toastError('Failed to update template');
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!teamId) return;
    const wasSelected = widgetConfig && widgetConfig.widget_template_id === templateId;
    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      await axios.delete(`/teams/${teamId}/widget_templates/${templateId}`);
      onWidgetTemplatesChange(widgetTemplates.filter((t) => t.id !== templateId));
      if (wasSelected) {
        // The DB's dependent: :nullify already cleared widget_template_id.
        // Mirror that locally without touching appearance columns — the
        // visible style stays put as overrides on top of Opendate Default,
        // and the picker flips to "Opendate Default · Edited".
        onWidgetConfigChange({ ...widgetConfig, widget_template_id: null });
      }
    } catch (e) {
      console.error('AppearanceTab deleteTemplate failed', e);
      toastError('Failed to delete template');
    }
  };

  const toggleSection = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const cornerRadiusValue = form.corner_radius === null || form.corner_radius === undefined
    ? DEFAULTS.corner_radius
    : form.corner_radius;

  const fontFamilyValue = form.font_family === null || form.font_family === undefined
    ? DEFAULTS.font_family
    : form.font_family;
  const selectedFontFamily = fontFamilyValue.split(',')[0].trim();

  return (
    <div className="cwb-appearance-tab">
      <div className="cwb-theme-block">
        <ThemePicker
          widgetConfig={widgetConfig}
          widgetTemplates={widgetTemplates}
          isEdited={isEdited}
          onApply={applyTemplate}
          onDelete={deleteTemplate}
        />

        {isEdited && (
          <div className="cwb-theme-actions">
            <Button variant="secondary" size="sm" onClick={() => setSaveModalOpen(true)}>
              Save as New
            </Button>
            {selectedTemplate && (
              <Button variant="primary" size="sm" onClick={updateTemplate}>
                {(selectedTemplate.usage_count || 0) > 1
                  ? `Update Theme · used by ${selectedTemplate.usage_count} events`
                  : 'Update Template'}
              </Button>
            )}
          </div>
        )}
      </div>

      {saveModalOpen && (
        <CreateEntityModal
          title="Save Appearance Template"
          subtitle="Give your theme a name so you can reuse it later."
          placeholder="e.g. Dark Mode, Holiday Theme..."
          buttonLabel="Save Template"
          onSubmit={saveAsTemplate}
          onClose={() => setSaveModalOpen(false)}
        />
      )}

      <Section
        label="Colors"
        open={expanded.colors}
        onToggle={() => toggleSection('colors')}>
        <div className="cwb-color-mode-toggle">
          <Toggle
            id="cwb-advanced-colors"
            size="sm"
            label="Advanced colors"
            checked={advanced}
            onChange={(e) => setMode(e.target.checked ? 'advanced' : 'simple')}
          />
        </div>

        {!advanced ? (
          SIMPLE_FIELDS.map(({ canonical, label }) => (
            <ColorRow
              key={canonical}
              label={label}
              value={colorValue(form, canonical)}
              onChange={(v) => simpleUpdate(canonical, v)}
            />
          ))
        ) : (
          ADVANCED_SECTIONS.map((section) => (
            <div key={section.label} className="cwb-advanced-color-group">
              <div className="cwb-advanced-color-group-label">{section.label}</div>
              {section.fields.map(({ key, label }) => (
                <ColorRow
                  key={key}
                  label={label}
                  value={colorValue(form, key)}
                  onChange={(v) => update(key, v)}
                />
              ))}
            </div>
          ))
        )}
      </Section>

      <Section
        label="Typography"
        open={expanded.typography}
        onToggle={() => toggleSection('typography')}>
        <Select
          id="cwb-font-family"
          label="Font family"
          size="sm"
          options={FONT_FAMILIES.map((f) => ({ value: f, label: f }))}
          value={selectedFontFamily}
          onChange={(fontFamily) => {
            if (fontFamily !== selectedFontFamily) update('font_family', fontFamily);
          }}
        />
      </Section>

      <Section
        label="Corner radius"
        open={expanded.cornerRadius}
        onToggle={() => toggleSection('cornerRadius')}>
        <input
          type="range"
          className="cwb-corner-radius-slider"
          min={0}
          max={24}
          value={cornerRadiusValue}
          onChange={(e) => update('corner_radius', Number(e.target.value))}
        />
        <div className="cwb-corner-radius-value">{cornerRadiusValue}px</div>
      </Section>

      {pending && <div className="cwb-appearance-status">Saving…</div>}
    </div>
  );
};

const Section = ({ label, open, onToggle, children }) => (
  <div className={`cwb-appearance-section ${open ? 'open' : ''}`}>
    <button
      type="button"
      className="cwb-appearance-section-header"
      onClick={onToggle}>
      <span>{label}</span>
      <ChevronDown width={16} height={16} aria-hidden="true" className="cwb-appearance-section-chevron" />
    </button>
    <div className="cwb-appearance-section-body">{children}</div>
  </div>
);

const ColorRow = ({ label, value, onChange }) => {
  const [hexDraft, setHexDraft] = useState(value);

  useEffect(() => { setHexDraft(value); }, [value]);

  const commitHex = () => {
    const next = normalizeHex(hexDraft, value);
    setHexDraft(next);
    if (next !== value) onChange(next);
  };

  return (
    <div className="cwb-color-row">
      <span className="cwb-color-row-label">{label}</span>
      <div className="cwb-color-controls">
        <span className="cwb-color-swatch" style={{ background: value }}>
          <input
            type="color"
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        </span>
        <input
          type="text"
          className="cwb-color-hex"
          value={hexDraft}
          onChange={(e) => setHexDraft(e.target.value)}
          onBlur={commitHex}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.currentTarget.blur();
            } else if (e.key === 'Escape') {
              setHexDraft(value);
              e.currentTarget.blur();
            }
          }}
        />
      </div>
    </div>
  );
};

export default AppearanceTab;
